import { useEffect, useState } from "react";
import Base from "../../Base/views/Base";
import {
    Box,
    Button,
    Card,
    Grid,
    Typography,
    TextField,
    IconButton
} from "@mui/material";
import Popup from "../../components/Popup";
import Swal from "sweetalert2";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from "../../../axiosInstance";

const InventoryCategoryLandingPage = () => {
    const [hotelID] = useState(localStorage.getItem("billing_app_hotel"));
    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);

    const initialValues = {
        name: "",
        description: "",
        hotel: ""
    };

    const [inputData, setInputData] = useState(initialValues);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        axiosInstance(`/api/inventory-category/?hotel=${hotelID}`, {
            method: "GET"
        }).then(res => {
            setData(res.data);
        }).catch(err => {
            console.error(err);
        });
    };

    const validate = () => {
        let temp = { ...errors };
        if (!inputData.name) temp.name = "Name is required";
        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputData({ ...inputData, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const handleClose = () => {
        setOpen(false);
        setInputData(initialValues);
        setErrors({});
    };

    const handleSubmit = () => {
        if (validate()) {
            const api = inputData?.id ? `/api/inventory-category/${inputData.id}/` : `/api/inventory-category/`;
            const method = inputData?.id ? "PUT" : "POST";

            axiosInstance(api, {
                method: method,
                data: {
                    name: inputData.name,
                    description: inputData.description,
                    hotel: hotelID
                }
            }).then(res => {
                getData();
                setOpen(false);
                setInputData(initialValues);
                Swal.fire({
                    icon: 'success',
                    title: inputData?.id ? 'Category updated!' : 'Category added!',
                    timer: 1500
                });
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to save category'
                });
            });
        }
    };

    const handleClick = (item) => {
        setInputData(item);
        setOpen(true);
    };

    const handleDelete = (e, id) => {
        e?.stopPropagation();
        Swal.fire({
            title: "Are you sure?",
            text: "This category will be deleted.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            icon: 'warning',
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance(`/api/inventory-category/${id}/`, {
                    method: "DELETE"
                }).then(res => {
                    getData();
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        timer: 1500
                    });
                }).catch(err => {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: err?.response?.data?.detail || 'Failed to delete category. It may be in use.'
                    });
                });
            }
        });
    };

    return (
        <Base>
            <Popup
                open={open}
                handleClose={handleClose}
                title={inputData?.id ? "Edit Category" : "Add Category"}
                handleSubmit={handleSubmit}
                save={true}
                maxWidth="sm"
            >
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="Category Name"
                        fullWidth
                        required
                        name="name"
                        value={inputData.name || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                        error={!!errors.name}
                        helperText={errors.name}
                    />

                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        name="description"
                        value={inputData.description || ""}
                        onChange={handleChange}
                        size="small"
                    />
                </Box>
            </Popup>

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpen(true)}
                    size="small"
                    sx={{
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(139, 68, 255, 0.3)',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(139, 68, 255, 0.4)',
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Add Category
                </Button>
            </Box>

            <Grid container spacing={2}>
                {data.length === 0 ? (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                mt: 8,
                                textAlign: "center",
                                py: 8,
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'text.secondary',
                                    mb: 2,
                                    fontWeight: 500,
                                }}
                            >
                                No categories found
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                    mb: 4,
                                }}
                            >
                                Create your first inventory category to organize items
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => setOpen(true)}
                                sx={{
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Add Your First Category
                            </Button>
                        </Box>
                    </Grid>
                ) : (
                    data.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <Card
                                sx={{
                                    p: 1.5,
                                    cursor: "pointer",
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    "&:hover": {
                                        boxShadow: '0 4px 12px rgba(139, 68, 255, 0.15)',
                                        transform: "translateY(-3px)",
                                        borderColor: "primary.main",
                                    }
                                }}
                                onClick={() => handleClick(item)}
                            >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                                    <Box>
                                        <Typography variant="h6">{item.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.description || "No description"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            handleClick(item);
                                        }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(e, item.id);
                                        }}>
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>
        </Base>
    );
};

export default InventoryCategoryLandingPage;
